import { useRef, useState } from "react";
import type { Table } from "@tanstack/react-table";
import moment from "moment-timezone";
import { ChevronLeft, ChevronRight, ExternalLink, Eye, RefreshCw } from "lucide-react";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { CommonDestinationTimezone } from "@/lib/utils";
import {
  getDocumentDispatchPreview,
  type DocumentDispatchListItemDto,
  type DocumentDispatchListQuery,
  useApproveDocumentDispatch,
  useBulkSendDocumentDispatch,
  useHideDocumentDispatch,
  useListOtcDocumentDispatches,
  useRetryDocumentDispatch,
  useSendDocumentDispatch,
} from "@/lib/api/otcDocuments";
import {
  ApproveDocumentDispatchRequestValidation,
  BulkSendDocumentDispatchRequestValidation,
  HideDocumentDispatchRequestValidation,
  RetryDocumentDispatchRequestValidation,
  SendDocumentDispatchRequestValidation,
} from "@/lib/validation/otcDocuments";
import { IndeterminateCheckbox } from "@/section/cfr/components/IndeterminateCheckbox";
import {
  documentDispatchRowBgColor,
  documentDispatchStatusLegend,
} from "./documentDispatchRowColors";
import { DocumentDispatchEversignStatusReportDialog } from "./DocumentDispatchEversignStatusReportDialog";

type QueueTab = "automated" | "requires_review" | "sent" | "archived";
type DocumentType = "original" | "signed";
type DocumentAction = "preview" | "open";

const PAGE_SIZE = 100;

const tabFilters: Record<QueueTab, Partial<DocumentDispatchListQuery>> = {
  automated: { preset: "automated_queued" },
  requires_review: { preparation_status: "ready", review_status: "pending", visibility_status: "visible" },
  sent: { queue_status: "submitted" },
  archived: { visibility_status: "hidden" },
};

function titleCase(value: string): string {
  return value
    .split("_")
    .map((word: string) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`)
    .join(" ");
}

function rowIdentity(row: DocumentDispatchListItemDto): string {
  const info = row.OtcAmendmentInfo;
  return `${info?.Customer.long_name || "Unknown customer"}, contract ${info?.TradingContract.contract_no || "—"}, amendment ${info?.amendmentNumber ?? "—"}`;
}

function canApprove(row: DocumentDispatchListItemDto): boolean {
  return row.hiddenAt === null
    && row.preparationStatus === "ready"
    && row.requiresReview
    && row.approvedAt === null
    && row.sentAt === null
    && row.deliveryStatus !== "submitted";
}

function canRetry(row: DocumentDispatchListItemDto): boolean {
  return row.preparationStatus === "failed"
    && row.sentAt === null
    && row.OtcContractDocumentInstance === null
    && row.amendmentChangeType !== null;
}

function canShowOriginal(row: DocumentDispatchListItemDto): boolean {
  return row.signing?.originalDocument?.available === true;
}

function canShowSigned(tab: QueueTab, row: DocumentDispatchListItemDto): boolean {
  return (tab === "sent" || tab === "archived")
    && row.signing?.signedDocument?.available === true
    && row.OtcContractDocumentInstance !== null;
}

export function OtcDocumentQueuePage() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<QueueTab>("automated");
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(null);
  const [documentLoadingId, setDocumentLoadingId] = useState<string | null>(null);
  const [actionsRow, setActionsRow] = useState<DocumentDispatchListItemDto | null>(null);
  const [sendRow, setSendRow] = useState<DocumentDispatchListItemDto | null>(null);
  const [hideRow, setHideRow] = useState<DocumentDispatchListItemDto | null>(null);
  const [actionRowId, setActionRowId] = useState<string | null>(null);
  const [bulkSendIds, setBulkSendIds] = useState<string[]>([]);
  const bulkSendTableRef = useRef<Table<DocumentDispatchListItemDto> | null>(null);

  const query: DocumentDispatchListQuery = {
    feature_type: "otc_portal_amendment",
    page,
    page_size: PAGE_SIZE,
    ...tabFilters[activeTab],
  };
  const listResult = useListOtcDocumentDispatches(query);
  const listData = listResult.data?.success ? listResult.data.data : null;
  const rows = listData?.items ?? [];
  const total = listData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const approveDispatch = useApproveDocumentDispatch();
  const retryDispatch = useRetryDocumentDispatch();
  const sendDispatch = useSendDocumentDispatch();

  const showFailure = (
    title: string,
    result: { message?: string } | null,
    fallbackMessage: string,
  ): void => {
    toast({
      title,
      description: result?.message || fallbackMessage,
      variant: "destructive",
    });
  };

  const handleDocument = async (
    row: DocumentDispatchListItemDto,
    documentType: DocumentType,
    action: DocumentAction,
  ): Promise<void> => {
    if (documentLoadingId !== null) return;
    const loadingId = `${row.id}-${documentType}-${action}`;
    const openedWindow = action === "open" ? window.open("", "_blank") : null;
    if (openedWindow) openedWindow.opener = null;
    setDocumentLoadingId(loadingId);
    try {
      const result = await getDocumentDispatchPreview(
        row.id,
        documentType === "signed" ? "signed" : "sent",
      );
      if (!result || !result.success) {
        openedWindow?.close();
        showFailure("Failed to open document", result, "The document is unavailable.");
        return;
      }
      if (action === "open") {
        if (openedWindow) openedWindow.location.href = result.data.url;
        else showFailure("Failed to open document", null, "The browser blocked the new tab.");
      } else {
        setPreview({ url: result.data.url, title: `${documentType === "signed" ? "Signed" : "Original"} PDF — ${rowIdentity(row)}` });
      }
    } catch (error: unknown) {
      openedWindow?.close();
      toast({ title: "Failed to open document", description: "Failed to retrieve the document.", variant: "destructive" });
    } finally {
      setDocumentLoadingId(null);
    }
  };

  const handleApprove = async (row: DocumentDispatchListItemDto): Promise<void> => {
    const approveRequest = ApproveDocumentDispatchRequestValidation.safeParse({ queue_entry_id: row.id });
    const sendRequest = SendDocumentDispatchRequestValidation.safeParse({ queue_entry_id: row.id, is_test_send: false });
    if (!approveRequest.success || !sendRequest.success || actionRowId !== null) return;
    setActionRowId(row.id);
    try {
      const approveResult = await approveDispatch(approveRequest.data);
      if (!approveResult.success) {
        showFailure("Failed to approve document", approveResult, "Failed to approve the document.");
        return;
      }

      const sendResult = await sendDispatch(sendRequest.data);
      if (!sendResult.success) {
        showFailure("Failed to queue to send", sendResult, "Failed to queue to send.");
        return;
      }

      toast({
        title: "Approved and Queued for sending",
        description: `Contract #${row.OtcAmendmentInfo?.TradingContract.contract_no ?? "—"}, Amendment #${row.OtcAmendmentInfo?.amendmentNumber ?? "—"}`,
      });
    } finally {
      setActionRowId(null);
    }
  };

  const handleRetry = async (row: DocumentDispatchListItemDto): Promise<void> => {
    const parsed = RetryDocumentDispatchRequestValidation.safeParse({ queue_entry_id: row.id });
    if (!parsed.success || actionRowId !== null) return;
    setActionRowId(row.id);
    try {
      const result = await retryDispatch(parsed.data);
      if (result.success) toast({ title: "Generation retry queued", description: result.message });
      else showFailure("Failed to retry generation", result, "Failed to retry document generation.");
    } finally {
      setActionRowId(null);
    }
  };

  const bulkSendEnabled = activeTab === "automated" || activeTab === "requires_review";
  const columns: DataTableColumn<DocumentDispatchListItemDto>[] = [];
  if (bulkSendEnabled) {
    columns.push({
      id: "selection-box",
      enableFiltering: false,
      enableSorting: false,
      enableGrouping: false,
      disableRowClick: true,
      header: ({ table }) => (
        <IndeterminateCheckbox
          className="mt-2 ml-1"
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <IndeterminateCheckbox
          className="ml-1"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          indeterminate={row.getIsSomeSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      size: 50,
    });
  }
  if (activeTab === "automated" || activeTab === "sent" || activeTab === "archived") {
    columns.push({
      id: "preview",
      header: "Preview",
      enableFiltering: false,
      enableSorting: false,
      disableRowClick: true,
      cell: ({ row }) => activeTab === "sent" || activeTab === "archived"
        ? canShowSigned(activeTab, row.original) ? (
          <Button size="sm" variant="outline" disabled={documentLoadingId !== null} onClick={() => handleDocument(row.original, "signed", "preview")}>
            <Eye className="mr-1 h-3 w-3" /> Preview Signed
          </Button>
        ) : null
        : canShowOriginal(row.original) ? (
          <Button size="sm" variant="outline" disabled={documentLoadingId !== null} onClick={() => handleDocument(row.original, "original", "preview")}>
            <Eye className="mr-1 h-3 w-3" /> Preview Original
          </Button>
        ) : null,
    });
  }
  columns.push(
    {
      id: "actions",
      header: "Actions",
      enableFiltering: false,
      enableSorting: false,
      disableRowClick: true,
      cell: ({ row }) => (
        <Button size="sm" variant="outline" onClick={() => setActionsRow(row.original)}>
          Actions
        </Button>
      ),
    },
    { id: "customer", accessorFn: (row: DocumentDispatchListItemDto) => row.OtcAmendmentInfo?.Customer.long_name ?? "", header: "Customer", size: 220 },
    { id: "contract", accessorFn: (row: DocumentDispatchListItemDto) => row.OtcAmendmentInfo?.TradingContract.contract_no ?? "", header: "Contract #" },
    { id: "amendment", accessorFn: (row: DocumentDispatchListItemDto) => row.OtcAmendmentInfo?.amendmentNumber ?? "", header: "Amendment #" },
    { id: "request_number", accessorFn: (row: DocumentDispatchListItemDto) => row.requestNumber, header: "Request #" },
    // { id: "request_type", accessorFn: (row: DocumentDispatchListItemDto) => row.requestType, header: "Request Type" },
    { id: "preparation_status", accessorFn: (row: DocumentDispatchListItemDto) => titleCase(row.preparationStatus), header: "Preparation" },
    { id: "delivery_status", accessorFn: (row: DocumentDispatchListItemDto) => titleCase(row.deliveryStatus), header: "Delivery" },
    { id: "review_status", accessorFn: (row: DocumentDispatchListItemDto) => row.requiresReview ? row.approvedAt ? "Approved" : "Pending" : "Not required", header: "Review" },
  );
  if (["sent", "archived"].includes(activeTab || "")) {
    columns.push(
      { id: "eversign_status", accessorFn: (row: DocumentDispatchListItemDto) => row.signing?.lastStatus ?? "", header: "Eversign Status" },
      { id: "actual_send_method", accessorFn: (row: DocumentDispatchListItemDto) => row.actualSendMethod ?? "", header: "Send Method" },
        {
            id: "sent_at",
            accessorFn: (row: DocumentDispatchListItemDto) => row.sentAt,
            header: "Sent At",
            columnFilterType: "date",
            filterDateTimezone: CommonDestinationTimezone,
            filterDisplayFn: (value: unknown) => value ? moment.tz(value as string, CommonDestinationTimezone).format("YYYY/MM/DD") : "",
            filterParseDateFn: (value: unknown) => value ? moment.tz(value as string, CommonDestinationTimezone) : null,
            cell: ({ row }) => row.original.sentAt ? moment.tz(row.original.sentAt, CommonDestinationTimezone).format("MM/DD/YYYY h:mm A") : "",
            size: 190,
        },
    );
  }
  if (["archived"].includes(activeTab || "")) {
    columns.push(
        { id: "hidden_reason", accessorFn: (row: DocumentDispatchListItemDto) => row.hiddenReason ?? "", header: "Hidden Reason", size: 240 },
    );
  }
  columns.push(
    { id: "error", accessorFn: (row: DocumentDispatchListItemDto) => row.errorMessage ?? "", header: "Error", size: 240 },
    {
      id: "created_at",
      accessorFn: (row: DocumentDispatchListItemDto) => row.createdAt,
      header: "Created At",
      columnFilterType: "date",
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (value: unknown) => moment.tz(value as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
      filterParseDateFn: (value: unknown) => moment.tz(value as string, CommonDestinationTimezone),
      cell: ({ row }) => moment.tz(row.original.createdAt, CommonDestinationTimezone).format("MM/DD/YYYY h:mm A"),
      size: 190,
    },
  );

  const bulkButtons = bulkSendEnabled
    ? [({ table }: { table: Table<DocumentDispatchListItemDto> }) => {
        const selectedIds = table.getFilteredSelectedRowModel().rows.map(
          (row: { original: DocumentDispatchListItemDto }): string => row.original.id,
        );
        return (
          <Button
            variant="outline"
            disabled={selectedIds.length === 0}
            onClick={() => {
              bulkSendTableRef.current = table;
              setBulkSendIds(selectedIds);
            }}
          >
            Bulk Approve and Send ({selectedIds.length})
          </Button>
        );
      }]
    : undefined;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-sm uppercase tracking-wide text-muted-foreground">Document Queue</p>
        <h1 className="text-2xl font-bold">OTC Amendment PDFs</h1>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={activeTab} onValueChange={(value: string) => { setActiveTab(value as QueueTab); setPage(1); bulkSendTableRef.current?.resetRowSelection(); }}>
          <TabsList>
            <TabsTrigger value="automated">Automated</TabsTrigger>
            <TabsTrigger value="requires_review">Requires Review</TabsTrigger>
            <TabsTrigger value="sent">Sent</TabsTrigger>
            <TabsTrigger value="archived">Archived</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-center gap-2">
          <DocumentDispatchEversignStatusReportDialog
            featureType="otc_portal_amendment"
            title="Generate OTC Amendment Signing Report"
            triggerLabel="Generate OTC Amendment Signing Report"
          />
          <Button variant="outline" disabled={listResult.isFetching} onClick={() => listResult.refetch()}>
            <RefreshCw className={`mr-2 h-4 w-4 ${listResult.isFetching ? "animate-spin" : ""}`} />
            {listResult.isFetching ? "Refreshing..." : "Refresh"}
          </Button>
        </div>
      </div>
      {!listResult.isLoading && listResult.data && !listResult.data.success && (
        <p className="text-sm text-destructive">{listResult.data.message || "Failed to load OTC amendment PDF dispatches."}</p>
      )}
      <DataTable
        stickyHeaders
        tableClassName="min-h-[calc(40vh-100px)] max-h-[calc(100vh-360px)] overflow-y-scroll"
        defaultSorting={[{ id: "created_at", desc: true }]}
        data={rows}
        columns={columns}
        rowBgColor={activeTab === "automated" || activeTab === "sent" ? documentDispatchRowBgColor : undefined}
        legend={activeTab === "automated" || activeTab === "sent" ? documentDispatchStatusLegend : undefined}
        enableSorting
        enableMultiSort
        enableFiltering
        enableMultiFilter
        enableColumnVisibility
        enableMultiSelect={bulkSendEnabled}
        customToolbarButtons={bulkButtons}
        enableColumnResizing
        enableColumnPinning
        filterStyle="select"
        filterMode="internal"
        defaultCondensed
        emptyMessage={listResult.isLoading ? "Loading..." : "No OTC amendment PDF dispatches found"}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          {total === 0 ? "0 results" : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page === 1 || listResult.isFetching} onClick={() => { setPage((current: number) => current - 1); bulkSendTableRef.current?.resetRowSelection(); }}>
            <ChevronLeft className="h-4 w-4" /> Previous
          </Button>
          <span className="text-sm">Page {page} of {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages || listResult.isFetching} onClick={() => { setPage((current: number) => current + 1); bulkSendTableRef.current?.resetRowSelection(); }}>
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <ActionsDialog
        row={actionsRow}
        activeTab={activeTab}
        documentLoadingId={documentLoadingId}
        actionRowId={actionRowId}
        onClose={() => setActionsRow(null)}
        onDocument={handleDocument}
        onApprove={handleApprove}
        onSend={setSendRow}
        onRetry={handleRetry}
        onHide={setHideRow}
      />
      <DocumentPreviewDialog preview={preview} onClose={() => setPreview(null)} />
      <BulkSendDialog
        queueEntryIds={bulkSendIds}
        onClose={() => setBulkSendIds([])}
        onSent={() => {
          setBulkSendIds([]);
          bulkSendTableRef.current?.resetRowSelection();
        }}
      />
      <SendDialog row={sendRow} onClose={() => setSendRow(null)} />
      <HideDialog row={hideRow} onClose={() => setHideRow(null)} />
    </div>
  );
}

function ActionsDialog({
  row,
  activeTab,
  documentLoadingId,
  actionRowId,
  onClose,
  onDocument,
  onApprove,
  onSend,
  onRetry,
  onHide,
}: {
  row: DocumentDispatchListItemDto | null;
  activeTab: QueueTab;
  documentLoadingId: string | null;
  actionRowId: string | null;
  onClose: () => void;
  onDocument: (row: DocumentDispatchListItemDto, documentType: DocumentType, action: DocumentAction) => Promise<void>;
  onApprove: (row: DocumentDispatchListItemDto) => Promise<void>;
  onSend: (row: DocumentDispatchListItemDto) => void;
  onRetry: (row: DocumentDispatchListItemDto) => Promise<void>;
  onHide: (row: DocumentDispatchListItemDto) => void;
}) {
  const originalAvailable = row !== null && canShowOriginal(row);
  const signedAvailable = row !== null && canShowSigned(activeTab, row);

  return (
    <Dialog open={row !== null} onOpenChange={(open: boolean) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Document Actions</DialogTitle>
          <DialogDescription>{row ? rowIdentity(row) : ""}</DialogDescription>
        </DialogHeader>
        {row && (
          <div className="flex flex-wrap gap-2">
            {originalAvailable && (
              <>
                <Button size="sm" variant="outline" disabled={documentLoadingId !== null} onClick={() => { onClose(); onDocument(row, "original", "preview"); }}>
                  <Eye className="mr-1 h-3 w-3" /> Preview Original
                </Button>
                <Button size="sm" variant="outline" disabled={documentLoadingId !== null} onClick={() => onDocument(row, "original", "open")}>
                  <ExternalLink className="mr-1 h-3 w-3" /> Open Original
                </Button>
              </>
            )}
            {signedAvailable && (
              <>
                <Button size="sm" variant="outline" disabled={documentLoadingId !== null} onClick={() => { onClose(); onDocument(row, "signed", "preview"); }}>
                  <Eye className="mr-1 h-3 w-3" /> Preview Signed
                </Button>
                <Button size="sm" variant="outline" disabled={documentLoadingId !== null} onClick={() => onDocument(row, "signed", "open")}>
                  <ExternalLink className="mr-1 h-3 w-3" /> Open Signed
                </Button>
              </>
            )}
            {(["requires_review", "automated"].includes(activeTab)) && canApprove(row) && (
              <Button size="sm" disabled={actionRowId !== null} onClick={() => onApprove(row)}>Approve And Send</Button>
            )}
            {activeTab === "automated" && row.sendEligible && (
              <Button size="sm" disabled={actionRowId !== null} onClick={() => { onClose(); onSend(row); }}>Send</Button>
            )}
            {canRetry(row) && activeTab !== "archived" && (
              <Button size="sm" variant="outline" disabled={actionRowId !== null} onClick={() => onRetry(row)}>Retry</Button>
            )}
            {activeTab !== "archived" && row.hiddenAt === null && (
              <Button size="sm" variant="destructive" disabled={actionRowId !== null} onClick={() => { onClose(); onHide(row); }}>Hide</Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DocumentPreviewDialog({ preview, onClose }: { preview: { url: string; title: string } | null; onClose: () => void }) {
  return (
    <Dialog open={preview !== null} onOpenChange={(open: boolean) => { if (!open) onClose(); }}>
      <DialogContent className="flex h-[85vh] max-w-[85vw] flex-col">
        <DialogHeader><DialogTitle>{preview?.title || "Document Preview"}</DialogTitle></DialogHeader>
        <div className="flex-1 overflow-hidden">
          {preview && <iframe src={preview.url} title={preview.title} className="h-full w-full border-0" />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BulkSendDialog({
  queueEntryIds,
  onClose,
  onSent,
}: {
  queueEntryIds: string[];
  onClose: () => void;
  onSent: () => void;
}) {
  const { toast } = useToast();
  const sendDispatches = useBulkSendDocumentDispatch();
  const [sendType, setSendType] = useState<"test" | "live" | "">("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (): Promise<void> => {
    const parsed = BulkSendDocumentDispatchRequestValidation.safeParse({
      queue_entry_ids: queueEntryIds,
      is_test_send: sendType === "test",
      approve: true,
    });
    if (!parsed.success || !sendType || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await sendDispatches(parsed.data);
      if (!result.success) {
        toast({ title: "Failed to bulk queue document sends", description: result.message || "Failed to bulk queue the document sends.", variant: "destructive" });
      } else {
        toast({ title: sendType === "test" ? "Bulk test sends queued" : "Bulk live sends queued", description: `${result.data.enqueuedCount} document${result.data.enqueuedCount === 1 ? "" : "s"} queued.` });
        setSendType("");
        onSent();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={queueEntryIds.length > 0} onOpenChange={(open: boolean) => { if (!open && !isSubmitting) { setSendType(""); onClose(); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bulk Approve and Send OTC Amendment PDFs</DialogTitle>
          <DialogDescription>
            Approve any selected documents that require approval and queue {queueEntryIds.length} document{queueEntryIds.length === 1 ? "" : "s"} for sending.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label>Send type</Label>
          <Select value={sendType} onValueChange={(value: string) => setSendType(value as "test" | "live")} disabled={isSubmitting}>
            <SelectTrigger><SelectValue placeholder="Choose test or live send" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="test">Test send — deliver to internal ops</SelectItem>
              <SelectItem value="live">Live send — deliver to customer</SelectItem>
            </SelectContent>
          </Select>
          {sendType === "live" && <p className="font-medium text-destructive">This will send all selected documents to customers.</p>}
          {sendType === "test" && <p className="font-medium">This will perform sandbox test sends for all selected documents.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={isSubmitting} onClick={onClose}>Cancel</Button>
          <Button disabled={!sendType || isSubmitting} onClick={submit}>{isSubmitting ? "Queueing..." : "Confirm Bulk Send"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// In theory, this dialog is never used because documents automatically queue for sending after review.
// It remains available for documents that do not require approval or were approved but failed to queue for sending.
function SendDialog({ row, onClose }: { row: DocumentDispatchListItemDto | null; onClose: () => void }) {
  const { toast } = useToast();
  const sendDispatch = useSendDocumentDispatch();
  const [sendType, setSendType] = useState<"test" | "live" | "">("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (): Promise<void> => {
    const parsed = SendDocumentDispatchRequestValidation.safeParse({ queue_entry_id: row?.id, is_test_send: sendType === "test" });
    if (!parsed.success || !sendType || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await sendDispatch(parsed.data);
      if (!result.success) {
        toast({ title: "Failed to queue document send", description: result.message || "Failed to queue the document send.", variant: "destructive" });
      } else {
        toast({ title: sendType === "test" ? "Test send queued" : "Live send queued", description: result.message });
        setSendType("");
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={row !== null} onOpenChange={(open: boolean) => { if (!open && !isSubmitting) { setSendType(""); onClose(); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send OTC Amendment PDF</DialogTitle>
          <DialogDescription>{row ? rowIdentity(row) : ""}. Choose whether to perform a sandbox test send or a live customer send.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label>Send type</Label>
          <Select value={sendType} onValueChange={(value: string) => setSendType(value as "test" | "live")} disabled={isSubmitting}>
            <SelectTrigger><SelectValue placeholder="Choose test or live send" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="test">Test send — deliver to internal ops</SelectItem>
              <SelectItem value="live">Live send — deliver to customer</SelectItem>
            </SelectContent>
          </Select>
          {sendType === "live" && <p className="font-medium text-destructive">This is a live send to the customer.</p>}
          {sendType === "test" && <p className="font-medium">This is a sandbox test send.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={isSubmitting} onClick={onClose}>Cancel</Button>
          <Button disabled={!sendType || isSubmitting} onClick={submit}>{isSubmitting ? "Queueing..." : "Confirm Send"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HideDialog({ row, onClose }: { row: DocumentDispatchListItemDto | null; onClose: () => void }) {
  const { toast } = useToast();
  const hideDispatch = useHideDocumentDispatch("otc_portal_amendment");
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (): Promise<void> => {
    if (!row || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const parsed = HideDocumentDispatchRequestValidation.safeParse({ queue_entry_id: row.id, reason });
      if (!parsed.success) return;
      const result = await hideDispatch(parsed.data);
      if (!result.success) {
        toast({ title: "Failed to hide document", description: result.message || "Failed to hide the document.", variant: "destructive" });
      } else {
        toast({ title: "Document hidden", description: result.message });
        setReason("");
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={row !== null} onOpenChange={(open: boolean) => { if (!open && !isSubmitting) { setReason(""); onClose(); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Hide OTC Amendment PDF</DialogTitle>
          <DialogDescription>
            {row ? rowIdentity(row) : ""}. Preparation: {row?.preparationStatus || "—"}; delivery: {row?.deliveryStatus || "—"}. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="otc-document-hide-reason">Reason</Label>
          <Textarea id="otc-document-hide-reason" value={reason} onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setReason(event.target.value)} disabled={isSubmitting} placeholder="Enter a reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={isSubmitting} onClick={onClose}>Cancel</Button>
          <Button variant="destructive" disabled={!reason.trim() || isSubmitting} onClick={submit}>{isSubmitting ? "Hiding..." : "Hide"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
